import { MultiValue, SingleValue } from "react-select";
import { useMemo } from "react";
import { queryClient } from "@/ajax/queryClient";
import { StyledAsyncSelect } from "./StyledAsyncSelect";
import { useTRPC } from "@/trpc";
import { useOrganization } from "@/hooks/use-organization";

type ExtraProps = {
  placeholder?: string;
  disabled?: boolean;
};
type SingleSelect = {
  value: { name: string; id: string } | null;
  onChange?: (value: { name: string; id: string } | null) => void;
  isMulti: false;
} & ExtraProps;
type MultiSelect = {
  value: { name: string; id: string }[];
  onChange?: (value: { name: string; id: string }[]) => void;
  isMulti: true;
} & ExtraProps;

type ProviderPoolSelectProps = SingleSelect | MultiSelect;

export const ProviderPoolSelect = (props: ProviderPoolSelectProps) => {
  const propsWithDefaults = {
    placeholder: "Select provider pool",
    disabled: false,
    ...props,
  };

  const trpc = useTRPC();
  const { selectedOrgBusinessUnit } = useOrganization();
  const providerPoolResult = useMemo(() => {
    return queryClient.fetchQuery(
      trpc.providerPool.list.queryOptions({
        organizationBusinessUnitId: selectedOrgBusinessUnit?.id || "",
      }),
    );
  }, []);

  const loadProviderPools = async (inputValue: string) => {
    const providerPoolOptions = await providerPoolResult;
    const lowerInput = inputValue.toLowerCase();
    return providerPoolOptions
      .map((providerPool) => ({
        label: providerPool.name,
        value: providerPool.id,
      }))
      .map((option) => ({
        ...option,
        score: option.label.toLowerCase().indexOf(lowerInput),
      }))
      .filter((option) => option.score >= 0)
      .sort((a, b) => {
        // Exact matches first
        if (a.score === 0 && b.score !== 0) {
          return -1;
        }
        if (b.score === 0 && a.score !== 0) {
          return 1;
        }
        // Then by position of match
        return a.score - b.score;
      })
      .map((option) => ({
        label: option.label,
        value: option.value,
      }));
  };

  const handleChange = (
    newValue:
      | MultiValue<{ label: string; value: string }>
      | SingleValue<{ label: string; value: string }>,
  ) => {
    if (propsWithDefaults.isMulti) {
      const value = newValue as MultiValue<{ label: string; value: string }>;
      propsWithDefaults.onChange?.(
        value ? value.map((v) => ({ name: v.label, id: v.value })) : [],
      );
    } else {
      const value = newValue as SingleValue<{ label: string; value: string }>;
      propsWithDefaults.onChange?.(
        value ? { name: value.label, id: value.value } : null,
      );
    }
  };

  let selectValue;
  if (propsWithDefaults.isMulti) {
    if (propsWithDefaults.value) {
      selectValue = propsWithDefaults.value.map((v) => ({
        label: v.name,
        value: v.id,
      }));
    } else {
      selectValue = [];
    }
  } else {
    if (propsWithDefaults.value) {
      selectValue = {
        label: propsWithDefaults.value.name,
        value: propsWithDefaults.value.id,
      };
    } else {
      selectValue = undefined;
    }
  }

  return (
    <StyledAsyncSelect
      value={selectValue}
      loadOptions={loadProviderPools}
      onChange={handleChange}
      defaultOptions={true}
      isDisabled={props.disabled}
      placeholder={props.placeholder}
      isMulti={props.isMulti}
      isClearable
    />
  );
};
